<template>
	<div class="no-outline">
		<div class="flex xs-between xs-middle pb6 pt6">
			<div class="fs6 fw7">{{ $t("file_browser") }}</div>
			<!-- search file -->
			<j-button size="xsmall" @click="openSearchFileModal">{{
				$t("search_file")
			}}</j-button>
		</div>
		<j-card class="xs-12 bg5 flex wrap">
			<!-- Menu bar -->
			<div class="flex xs-12 bb1 bc7">
				<!-- Navigation -->
				<div class="flex xs-8">
					<!-- Back button -->
					<div class="cu2 flex xs-middle xs-center p2" @click="onClickBack()">
						<crm-icon name="back" />
					</div>
					<!-- Path -->
					<div class="bg4 m1 b1 r2 bc7 flex xs-middle c6 fs3">
						<div
							v-for="(step, i) in computedPath"
							:key="step.key"
							:id="step.key"
							:class="[
								'draggable p1 c1 no-select no-outline',
								{ 'bg5 fw7': isHoveredFolder(step) },
								{ 'br1 bc7': computedPath.length - 1 !== i }
							]"
						>
							<div class="p1 cu2" @click="goToStep(step)">{{ step.name }}</div>
						</div>
					</div>
				</div>
				<!-- Actions -->
				<div class="flex xs-4 xs-right">
					<div
						class="cu2 flex xs-middle xs-center p2 bl1 bc7"
						@click="fetchFolder()"
						v-tooltip="$t('refresh')"
					>
						<crm-icon name="refresh" />
					</div>

					<div
						class="cu2 flex xs-middle xs-center p2 bl1 bc7"
						@click="pasteFile(currentFolder)"
						v-tooltip="$t('paste')"
					>
						<crm-icon :color="cutItem ? 'c1' : 'c7'" name="paste" />
					</div>
					<div
						v-can="'writeFiles'"
						class="cu2 flex xs-middle xs-center p2 bl1 bc7"
						v-tooltip="$t('create_folder')"
						@click="openCreateFolderModal"
					>
						<crm-icon name="create-folder" />
					</div>
					<div
						v-can="'writeFiles'"
						class="cu2 flex xs-middle xs-center p2 bl1 bc7"
						v-tooltip="$t('upload_file')"
						@click="openUploadModal"
					>
						<crm-icon name="upload" />
					</div>
				</div>
			</div>

			<!-- Details view -->
			<div class="xs-12 file-browser-content" id="content">
				<!-- Header -->
				<div class="flex xs-12 bb1 bc7 fs3 p2 c6">
					<div class="xs-7">{{ $t("name") }}</div>
					<div class="xs-2">{{ $t("size") }}</div>
					<div class="xs-2 flex">{{ $t("modified") }}</div>
					<div class="xs-1 flex xs-right">{{ $t("actions") }}</div>
				</div>
				<!-- Spinner -->
				<div v-if="isLoading" class="xs-12 flex xs-center p4">
					<j-spinner :delay="0" />
				</div>

				<!-- List -->
				<div
					v-for="item in computedList"
					:key="item.key"
					:id="item.key"
					class="flex xs-12 cu2 p2 xs-middle no-select"
					:class="[
						item.class,
						{ 'bg2 c5': isSelected(item) },
						{ bg4: isHoveredFolder(item) },
						{ 'cut-item': isCut(item) }
					]"
					@click="onClickItem(item)"
					@dblclick="onDoubleClick(item)"
				>
					<div class="flex xs-7 xs-middle">
						<crm-icon
							v-if="item.icon"
							:color="
								isSelected(item) ? 'c5' : item.type === 'folder' ? 'c6' : 'c1'
							"
							:name="item.icon"
							size="normal"
						/>
						<div
							class="fs3 fw7 pb1"
							:class="{
								c6: item.type === 'folder' && !isSelected(item)
							}"
						>
							{{ item.name }}
						</div>
					</div>
					<div class="flex xs-2 fs3">{{ item.size }}</div>
					<div class="flex xs-2 fs3 flex">
						{{ item.lastModifiedAt }}
					</div>
					<div class="xs-1 flex xs-right">
						<file-browser-context-menu
							:item="item"
							:isSelected="isSelected(item)"
							:cutItem="cutItem"
							@item:download="downloadFile(item.key, item.name)"
							@item:open="onDoubleClick(item)"
							@item:rename="renameItem(item)"
							@item:cut="cutItem = item"
							@item:paste="pasteFile(item.key)"
							@item:privacy="editPrivacyItem(item)"
							@item:delete="openDeleteDialog(item)"
						/>
					</div>
				</div>
				<!-- No data -->
				<div
					v-if="!isLoading && !computedList.length"
					class="flex xs-12 xs-middle xs-center p10"
				>
					<div class="c6">{{ $t("errors.no_data") }}</div>
				</div>
			</div>

			<file-browser-selected-section
				v-if="selectedItem"
				:selectedItem="selectedItem"
			/>
		</j-card>

		<!-- Delete Confirmation Modal -->
		<j-dialog
			:title="$t('delete_file')"
			type="delete"
			@confirm="deleteItem(selectedItem)"
			>{{ $t("errors.are_you_sure_you_want_to_delete_file") }}</j-dialog
		>

		<!-- Modal -->
		<core-modal
			name="fileSystemModal"
			:headline="modalComponent.title"
			:width="modalComponent.width"
			:key="modalComponent.width"
		>
			<component
				:is="modalComponent.name"
				v-bind="modalComponent.props"
				v-on="modalComponent.handlers"
			/>
		</core-modal>
	</div>
</template>

<script>
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import JSpinner from "@/components/JSpinner"
import JCard from "@/components/JCard"
import JDialog from "@/components/JDialog"
import JButton from "@/components/JButton"
import JBaseInputText from "@/components/JBaseInputText"
import CrmIcon from "@/components/CrmIcon"
import CoreModal from "@/modules/core/components/CoreModal"
import CoreFilesUpload from "@/modules/core/components/CoreFilesUpload"
import CreateFolderInput from "./CreateFolderInput.vue"
import RenameFileInput from "./RenameFileInput.vue"
import SearchFileInput from "./SearchFileInput.vue"
import EditPrivacyFileInput from "./EditPrivacyFileInput"

import FileBrowserSelectedSection from "./FileBrowserSelectedSection"
import FileBrowserContextMenu from "./FileBrowserContextMenu"

import dayjs from "dayjs"
import FileSaver from "file-saver"
import axios from "axios"
import { Draggable } from "@shopify/draggable"

export default {
	name: "FileBrowser",

	props: {
		bucketName: null
	},

	components: {
		JSpinner,
		JCard,
		JDialog,
		JButton,
		JBaseInputText,
		CrmIcon,
		CoreModal,
		CoreFilesUpload,
		CreateFolderInput,
		RenameFileInput,
		FileBrowserSelectedSection,
		FileBrowserContextMenu,
		EditPrivacyFileInput,
		SearchFileInput
	},

	data() {
		return {
			isLoading: false,
			modalComponent: {
				name: null,
				props: null
			},
			selectedItem: null,
			currentFolder: null,
			files: [], // objectSummaries = Fetched list of files inside current folder
			subfolders: [], // commonPrefixes = List of folder inside current folder

			folderHoveredByDraggable: null,

			// Keep item to move using shortcuts
			cutItem: null
		}
	},

	computed: {
		computedList() {
			const computedList = []
			// Compute folders
			this.subfolders.map(item =>
				computedList.push({
					key: item,
					icon: "folder",
					type: "folder",
					class: "draggable",
					name: item.split("/")[item.split("/").length - 2]
				})
			)
			// Compute files
			this.files
				.filter(item => item?.fileName?.length)
				.map(item =>
					computedList.push({
						key: item.key,
						type: "file",
						class: "draggable",
						icon: this.decideIcon(item.fileName),
						name: item.fileName,
						size: this.formatBytes(item.size),
						isPublic: item.isPublic,
						lastModifiedAt: item.lastModifiedAt
							? dayjs(item.lastModifiedAt).format(
									this.$store.state.settings.dateTimeFormat
							  )
							: null
					})
				)
			return computedList
		},

		computedPath() {
			if (!this?.currentFolder && this.currentFolder.length)
				return [{ name: this.bucketName, key: "" }]
			let path = ""
			const array =
				this.currentFolder.split("/").map(step => {
					path = path + step + "/"
					return {
						name: step,
						key: path
					}
				}) || []
			array.pop()
			array.unshift({ name: this.bucketName, key: "" })
			return array
		}
	},

	methods: {
		decideIcon(name) {
			if (typeof name !== "string") return null
			const extension = name.split(".").pop()
			if (["png", "jpg", "bmp", "svg"].includes(extension))
				return "file-type-image"
			if (["mp3", "wma", "wav", "webm", "flac"].includes(extension))
				return "file-type-music"
			if (["odt", "dot", "dotx", "docx", "doc", "rtf"].includes(extension))
				return "file-type-text"
			if (
				[
					"ods",
					"csv",
					"xla",
					"xls",
					"xlav",
					"xlsb",
					"xlsm",
					"xlsx",
					"xlt",
					"xltm",
					"xltx"
				].includes(extension)
			)
				return "file-type-chart"
			if (
				[
					"html",
					"htm",
					"xml",
					"js",
					"py",
					"java",
					"cpp",
					"cc",
					"cs",
					"ts",
					"go"
				].includes(extension)
			)
				return "file-type-code"
			if (["pdf"].includes(extension)) return "file-type-pdf2"
			return "policies"
		},

		onDoubleClick(item) {
			this.selectedItem = null
			if (item.type === "folder") {
				this.currentFolder = item.key
				this.fetchFolder()
			}
			if (item.type === "file") this.downloadFile(item.key, item.name)
		},

		onClickItem(item) {
			this.selectedItem = this.selectedItem?.key === item.key ? null : item
		},

		onClickBack() {
			if (!this.currentFolder?.length) {
				this.$router.push({ name: "FileSystemList" })
			}
			const keyAsArray = this.currentFolder.split("/")
			this.currentFolder =
				keyAsArray.length > 2
					? keyAsArray.splice(0, keyAsArray.length - 2).join("/") + "/"
					: ""
			this.fetchFolder()
		},

		goToStep(step) {
			this.currentFolder = step?.key
			this.fetchFolder()
		},

		// Open modal to enter a folder name
		// Handles the result from modal in the handler
		openCreateFolderModal() {
			this.modalComponent = {
				name: "CreateFolderInput",
				title: this.$t("create_folder"),
				width: 500,
				props: {
					filesOf: "custom",
					customPath: this.currentFolder.slice(0, -1)
				},
				handlers: {
					input: async input => {
						const query = gql`
							mutation createFolder($key: String!) {
								createFolder(key: $key) {
									errors
									errors_2 {
										code
										message
									}
									status
								}
							}
						`
						const variables = {
							key: this.currentFolder + input
						}
						this.isLoading = true
						const res = await api({ query, variables })
						this.isLoading = false
						this.statusIsSuccess(res, "createFolder")
						this.fetchFolder()
						this.$modal.hide("fileSystemModal")
					}
				}
			}
			this.$nextTick(() => {
				this.$modal.show("fileSystemModal")
			})
		},

		openDeleteDialog(item) {
			this.selectedItem = item
			this.$modal.show("j-dialog")
		},

		// Open modal to choose files and upload them (api calls in CoreFilesUpload inside modal)
		// Handles the result from modal in the handler
		openUploadModal() {
			this.modalComponent = {
				name: "CoreFilesUpload",
				title: this.$t("upload_files"),
				width: 800,
				props: {
					filesOf: "custom",
					customPath: encodeURIComponent(this.currentFolder.slice(0, -1)),
					showPrivacySelect: true
				},
				handlers: {
					"upload:finished": () => {
						this.$modal.hide("fileSystemModal")
						this.fetchFolder()
					}
				}
			}
			this.$nextTick(() => {
				this.$modal.show("fileSystemModal")
			})
		},

		// Open modal to search file in the bucket
		// Go to selected file
		openSearchFileModal() {
			this.modalComponent = {
				name: "SearchFileInput",
				title: this.$t("search_file"),
				width: 800,
				props: {
					bucketName: this.bucketName
				},
				handlers: {
					"file:found": file => {
						this.currentFolder = file?.key
						this.$modal.hide("fileSystemModal")
						this.fetchFolder()
						this.selectedItem = file
					}
				}
			}
			this.$nextTick(() => {
				this.$modal.show("fileSystemModal")
			})
		},

		async downloadFile(key, fileName) {
			this.isLoading = true
			axios({
				url: `https://api.asia.covergo.com/api/v1/files/${encodeURIComponent(key)}`,
				headers: {
					Authorization: `Bearer ${window.TK}`
				},
				method: "GET",
				responseType: "blob" // important
			})
				.then(response => {
					this.isLoading = false
					FileSaver.saveAs(
						new Blob([response.data], { type: response.data.type }),
						fileName
					)
				})
				.catch(e => {
					this.isLoading = false
					this.$store.dispatch("showError", [this.$t("errors.general")])
				})
		},

		// Open modal to rename a file
		// Handles the result from modal in the handler
		renameItem(item) {
			this.modalComponent = {
				name: "RenameFileInput",
				title: this.$t("rename_file"),
				width: 500,
				props: {
					fileName: item.name
				},
				handlers: {
					input: async input => {
						this.moveFile(item.key, this.currentFolder + input)
						this.$modal.hide("fileSystemModal")
					}
				}
			}
			this.$nextTick(() => {
				this.$modal.show("fileSystemModal")
			})
		},

		// Open modal to select a privacy level
		// Handles the result from modal in the handler
		async editPrivacyItem(item) {
			this.modalComponent = {
				name: "EditPrivacyFileInput",
				title: this.$t("edit_privacy_level"),
				width: 500,
				props: {
					isPublic: item.isPublic
				},
				handlers: {
					input: async input => {
						const query = gql`
							mutation setFileAclACL($isPublic: Boolean!, $key: String!) {
								setFileAcl(key: $key, isPublic: $isPublic) {
									errors
									status
									errors_2 {
										code
										message
									}
								}
							}
						`
						const variables = {
							key: item.key,
							isPublic: input
						}
						this.isLoading = true
						const res = await api({ query, variables })
						this.isLoading = false

						if (this.statusIsSuccess(res, "setFileAcl")) {
							this.fetchFolder()
							this.$modal.hide("fileSystemModal")
						}
					}
				}
			}
			this.$nextTick(() => {
				this.$modal.show("fileSystemModal")
			})
		},

		async deleteItem(item) {
			const apiKey = item?.type === "folder" ? "deleteFolder" : "deleteFile"
			const query = gql`
				mutation ${apiKey}($key: String!) {
					${apiKey}(key: $key) {
						errors
						errors_2 {
							code
							message
						}
						status
					}
				}
			`
			const variables = {
				key: item.type === "folder" ? item.key.slice(0, -1) : item.key
			}
			this.isLoading = true
			const res = await api({ query, variables })
			this.isLoading = false
			if (this.statusIsSuccess(res, apiKey)) {
				this.fetchFolder()
				this.selectedItem = null
			}
		},

		// Move a file with key fileKey to folder destinationKey
		async moveFile(key, newKey) {
			const query = gql`
				mutation moveFile($key: String!, $newKey: String!) {
					moveFile(key: $key, newKey: $newKey) {
						errors
						errors_2 {
							code
							message
						}
						status
					}
				}
			`
			const variables = {
				key,
				newKey
			}
			this.isLoading = true
			const res = await api({ query, variables })
			this.isLoading = false
			if (this.statusIsSuccess(res, "moveFile")) this.fetchFolder()
		},

		// Fetch all data for the case and save in store
		async fetchFolder() {
			// Clear current files / subfolders
			this.files = []
			this.subfolders = []
			this.selectedItem = null
			// TODO: fetch bucket using id from props
			this.isLoading = true
			const query = gql`
				query fileListing($prefix: String, $bucketName: String) {
					fileListing(prefix: $prefix, bucketName: $bucketName) {
						commonPrefixes
						objectSummaries {
							fileName
							key
							lastModifiedAt
							size
							isPublic
						}
					}
				}
			`

			const variables = {
				prefix: this.currentFolder,
				bucketName: this.bucketName
			}

			const res = await api({ query, variables })

			this.isLoading = false

			if (!res?.data?.fileListing)
				this.$store.dispatch("showError", ["Error when fetching the folder"])

			this.files = res?.data?.fileListing?.objectSummaries || []
			this.subfolders = res?.data?.fileListing?.commonPrefixes || []
		},

		// Helper to display pretty file size
		formatBytes(bytes) {
			if (bytes === 0) return "0 Bytes"
			const k = 1024
			const sizes = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
			const i = Math.floor(Math.log(bytes) / Math.log(k))
			return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
		},

		// Check if status of response is success, and display errors if any
		statusIsSuccess(res, apiKey) {
			if (res?.data?.[apiKey]?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: res?.errors
							? res?.errors
							: res?.data?.[apiKey]?.errors
							? res?.data?.[apiKey]?.errors
							: res?.data?.[apiKey]?.errors_2.message
							? res?.data?.[apiKey]?.errors_2.message
							: "Oops ! An error occured."
					}
				})
				return false
			} else {
				return true
			}
		},

		isSelected(item) {
			return this.selectedItem?.key === item.key
		},

		isHoveredFolder(item) {
			return item.key === this.folderHoveredByDraggable && item.type !== "file"
		},

		isCut(item) {
			return item.key === this?.cutItem?.key && item.type === "file"
		},

		pasteFile(folder) {
			if (!this.cutItem) {
				return
			}
			this.moveFile(
				this.cutItem.key,
				folder + this.cutItem.key?.split("/").pop()
			)
			this.cutItem = null
		},

		// Function to handle shortcuts
		shortcutHandler(e) {
			if (e.key === "x" && (e.ctrlKey || e.metaKey)) {
				e.preventDefault()
				this.cutItem =
					this.selectedItem?.type === "file"
						? JSON.parse(JSON.stringify(this.selectedItem))
						: null
			}
			// TODO: Handle ctrl + c the same way, need API to copy file
			if (e.key === "v" && (e.ctrlKey || e.metaKey)) {
				e.preventDefault()
				if (this.cutItem) {
					this.pasteFile(this.currentFolder)
				}
			}
		}
	},

	created() {
		// Fetch root of bucket
		this.currentFolder = ""
		this.fetchFolder()
	},

	mounted() {
		// ********* Handle dragging files into folders *********
		const draggable = new Draggable(this.$el, {
			draggable: ".draggable",
			mirror: {
				constrainDimensions: true
			}
		})
		// All files, folders and path need to have class 'draggable' to detect over, but we prevent dragging folders and path
		draggable.on("drag:start", event => {
			this.selectedItem = null
			if (
				!(
					this.computedList.find(item => item.key === event?.data?.source?.id)
						?.type === "file"
				)
			)
				event.cancel()
		})
		// Detect when a draggable is over an other draggable
		draggable.on("drag:over", event => {
			if (
				!(
					this.computedList.find(item => item.key === event?.data?.over?.id)
						?.type === "file"
				)
			) {
				this.folderHoveredByDraggable = event?.data?.over?.id
			}
		})
		// Detect when a draggable is released
		draggable.on("drag:stop", event => {
			if (this.folderHoveredByDraggable !== null) {
				this.moveFile(
					event?.data?.source?.id,
					this.folderHoveredByDraggable +
						event?.data?.source?.id?.split("/").pop()
				)
				this.folderHoveredByDraggable = null
			}
		})
		// Detect when a draggable stops hovering another draggable
		draggable.on("drag:out", () => {
			this.folderHoveredByDraggable = null
		})

		// ************* Handle shortcut to move items ****************
		document.addEventListener("keydown", this.shortcutHandler)
	},

	beforeDestroy() {
		document.removeEventListener("keydown", this.shortcutHandler)
	}
}
</script>

<style scoped>
.file-browser-content {
	height: 50vh;
	overflow-y: scroll;
}
.no-select {
	user-select: none;
}
.no-outline:focus {
	outline: none;
}
.draggable-source--is-dragging {
	width: 100%;
	background: none;
}
.draggable-dropzone--occupied {
	background: lightgrey;
}
.draggable-container {
	outline: 0;
}
.cut-item {
	opacity: 0.5;
}
</style>
